import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faStar, faStarHalfStroke, faArrowTrendUp, faArrowTrendDown, faArrowRight, faCheckCircle, faChevronDown, faChevronUp, faChevronLeft, faChevronRight, faXmark } from '@/fontawesome';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { APIRoutes } from '@/services/api.routes';
import { analytics, AnalyticsEvents } from '@/services/analytics';
import type { SubscriptionPriceProduct, SubscriptionType, EuroCent } from '@/models';
import { LANDING_PACKAGES, getBillingPeriodMonths } from '@/consts/pricingPublic';
import {
  LANDING_PAGE_CONTENT,
  parseLandingVariant,
  type LandingPageVariant,
  type LandingPageBenefits,
  type LandingPageApproach,
  type LandingPageWhatYouGet,
  type LandingPageKlartext,
  type LandingPageUnterschied,
  type LandingPageFaqItem,
  type LandingPageWhoTrades,
} from '@/consts/landingPageContent';
import { LANDING_PROMOTION_CONFIG, isPromotionActive } from '@/consts/promotion';
import PublicPricingCard, { type BillingPeriod } from '@/components/landing/PublicPricingCard';
import PromotionBanner from '@/components/PromotionBanner';
import { useSubscriptionProducts } from '@/contexts/SubscriptionProductsContext';
import { useDarkMode } from '@/hooks/useDarkMode';
import DarkModeToggle from '@/components/common/DarkModeToggle';
import logoTradingView from '@/assets/images/partner/tradingviewlogo-freelogovectors.net_.png';
import logoBitpanda from '@/assets/images/partner/Bitpanda-Logo-black-1024x285.png';
import logoXtb from '@/assets/images/partner/xtb.b8d7f549.svg';
import logoHamburg1 from '@/assets/images/partner/Hamburg-1-Logo_2012.svg';
import logoIBKR from '@/assets/images/partner/interactive_Brokers_Logo_(2014).svg.png';

/* URL-Varianten:
   /                        → Gesamtansicht (alle Pakete)
   /?paket=trade            → nur Trade
   /?paket=cashflow         → nur Cashflow
   /?paket=daytrading       → nur Daytrading
*/

import type { RecommendationCore, RecommendationEarnings, RecommendationSwingtrade, LongTermRecommendation, DayTradingSession as DayTradingSessionModel } from '@/models';
import { calculatePremiumPercentage } from '@/utils/helpers';
import { calculateDayTrade } from '@/utils/daytrade-calculations';
import { getProfitColorClass } from '@/utils/daytrade-session-profit';
import NumberInput from '@/components/forms/NumberInput';

type TradeCategory = 'Kernstrategie' | 'Earnings' | 'Swing Trade' | 'Langzeit' | 'Daytrading';

interface NormalizedTrade {
  symbol: string;
  result: 'win' | 'loss' | 'neutral';
  yieldPercent: number | null;
  yieldDecimals: number;
  statusLabel: string;
  closedAt: string;
  capitalPercent: number;
}

interface NormalizedSession {
  tradesCount: number;
  profitPct: number;
  sessionDate: string;
}

const CORE_EARNINGS_CAPITAL_PERCENT = 0.1;

function normalizeCoreRecommendation(rec: RecommendationCore): NormalizedTrade {
  const premiumPct = calculatePremiumPercentage(rec.premium, rec.strikePrice);
  return {
    symbol: rec.symbolTicker,
    result: rec.state === 'FINISHED_UNASSIGNED' ? 'win' : 'neutral',
    yieldPercent: premiumPct !== null ? premiumPct * 100 : null,
    yieldDecimals: 2,
    statusLabel: rec.state === 'FINISHED_UNASSIGNED' ? 'Erfolgreich' : 'Eingebucht',
    closedAt: rec.endDate,
    capitalPercent: CORE_EARNINGS_CAPITAL_PERCENT,
  };
}

function normalizeEarningsRecommendation(rec: RecommendationEarnings): NormalizedTrade {
  const premiumPct = calculatePremiumPercentage(rec.premium, rec.strikePrice);
  return {
    symbol: rec.symbolTicker,
    result: rec.state === 'FINISHED_UNASSIGNED' ? 'win' : 'neutral',
    yieldPercent: premiumPct !== null ? premiumPct * 100 : null,
    yieldDecimals: 2,
    statusLabel: rec.state === 'FINISHED_UNASSIGNED' ? 'Erfolgreich' : 'Eingebucht',
    closedAt: rec.endDate,
    capitalPercent: CORE_EARNINGS_CAPITAL_PERCENT,
  };
}

function normalizeSwingTrade(rec: RecommendationSwingtrade): NormalizedTrade {
  const sortedByTime = [...rec.adjustements].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
  let running = 0;
  let peak = 0;
  for (const adj of sortedByTime) {
    running += adj.delta;
    if (running > peak) peak = running;
  }
  return {
    symbol: rec.symbolTicker,
    result: rec.state === 'FINISHED_PROFIT' ? 'win' : 'loss',
    yieldPercent: rec.yield * 100,
    yieldDecimals: 1,
    statusLabel: rec.state === 'FINISHED_PROFIT' ? 'Gewinn' : 'Verlust',
    closedAt: rec.endTimestamp ?? rec.startTimestamp,
    capitalPercent: peak,
  };
}

function normalizeLongTermTrade(rec: LongTermRecommendation): NormalizedTrade {
  return {
    symbol: rec.symbolTicker,
    result: rec.state === 'FINISHED_PROFIT' ? 'win' : 'loss',
    yieldPercent: rec.yield * 100,
    yieldDecimals: 1,
    statusLabel: rec.state === 'FINISHED_PROFIT' ? 'Gewinn' : 'Verlust',
    closedAt: rec.endTimestamp ?? rec.startTimestamp,
    capitalPercent: 1,
  };
}

const SESSION_BUDGET = 50_000; // 50k USD

function normalizeSession(session: DayTradingSessionModel): NormalizedSession {
  const trades = session.state.trades;

  let totalProfit = 0;
  for (const trade of trades) {
    if (trade.state === 'OPEN') continue;
    if (trade.state === 'STOPPED') continue;

    const calc = calculateDayTrade({
      trade: trade,
      budget: SESSION_BUDGET * 100,
      mode: 'summary',
      processedAdjustements: [],
      skippedAdjustements: [],
      overrideAdjustementPrices: {},
      overrideAdjustmentStockCount: {},
      stockCountMode: 'NORMAL',
    });
    totalProfit += calc.profit;
  }

  return {
    tradesCount: trades.length,
    profitPct: (totalProfit / SESSION_BUDGET) * 100,
    sessionDate: session.start,
  };
}

const TRADE_TABS: TradeCategory[] = ['Kernstrategie', 'Earnings', 'Swing Trade', 'Daytrading'];

const PACKAGE_TRADE_CATEGORIES: Record<string, TradeCategory[]> = {
  trade:      ['Langzeit', 'Swing Trade'],
  cashflow:   ['Langzeit', 'Swing Trade', 'Earnings', 'Kernstrategie'],
  daytrading: ['Daytrading'],
};

interface TradesSectionProps {
  activeTab: TradeCategory;
  setActiveTab: (tab: TradeCategory) => void;
  visibleTabs: TradeCategory[];
  trades: Record<string, NormalizedTrade[]>;
  sessions: NormalizedSession[];
  loading: boolean;
  packageFilter: string[] | null;
  sinceDate: string;
  onSinceDateChange: (date: string) => void;
  earliestDates: Partial<Record<TradeCategory, string>>;
  hideCampusResults: boolean;
  approach: LandingPageApproach;
  whatYouGet: LandingPageWhatYouGet;
  klartext: LandingPageKlartext;
  unterschied: LandingPageUnterschied;
  calculatorDefaultCapital: number;
  calculatorNote: string | null;
  tradeTableNote: string | null;
}

function StrategyTabs({ activeTab, setActiveTab, visibleTabs }: { activeTab: TradeCategory; setActiveTab: (tab: TradeCategory) => void; visibleTabs: TradeCategory[] }) {
  return (
    <div className="flex flex-wrap items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full p-1 border border-gray-300 dark:border-gray-700 mx-auto w-fit mb-8">
      {visibleTabs.map((tab) => (
        <button
          key={tab}
          onClick={() => setActiveTab(tab)}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
            activeTab === tab
              ? 'bg-blue-600 text-white shadow-lg'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          {tab}
        </button>
      ))}
    </div>
  );
}

interface ChartDataPoint {
  date: string;
  dateLabel: string;
  dateMs: number;
  capital: number | null;
  capitalFuture: number | null;
  profit: number;
  isFuture: boolean;
}

const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;
const MAX_FUTURE_YEARS = 10;

const TRADES_PAGE_SIZE = 8;
const MIN_CAPITAL = 15_000;

const PACKAGE_CARDS = [
  {
    key: 'trade',
    title: 'AKTIV HANDELN',
    description: 'Du willst nicht nur halten, sondern gezielt Chancen nutzen und wissen, wann du rein- und rausgehst?',
    items: ['Swing-Trades', 'klare Ein- und Ausstiege', 'mittelfristige Chancen'],
  },
  {
    key: 'cashflow',
    title: 'EINNAHMEN AUFBAUEN',
    description: 'Du willst nicht nur Vermögen aufbauen, sondern regelmäßig Geld aus deinem Depot ziehen ohne dass es weniger wird?',
    items: ['wöchentliche Cashflow-Setups', 'Stillhalterstrategien', 'gezielte Earnings-Trades'],
  },
  {
    key: 'daytrading',
    title: 'DAYTRADING',
    description: 'Du handelst bereits – und willst intraday den nächsten Schritt gehen? Dann schau mir in Echtzeit über die Schulter.',
    items: ['börsentägliche Live-Session zum US-Opening (15:15–16:15 Uhr)', 'echte Entscheidungen am Markt – Einstieg, Management, Ausstieg', 'klare Einblicke ins Vorgehen: Warum dieser Trade? Warum jetzt? Warum diese Größe?', 'Möglichkeit Fragen zu stellen und Support zu erhalten'],
  },
] as const;

function ResultsSection({ activeTab, setActiveTab, visibleTabs, trades, sessions, loading, packageFilter, sinceDate, onSinceDateChange, earliestDates, hideCampusResults, approach, whatYouGet, klartext, unterschied, calculatorDefaultCapital, calculatorNote, tradeTableNote }: TradesSectionProps) {
  const [capital, setCapital] = useState<number>(calculatorDefaultCapital);
  const capitalIsValid = capital >= MIN_CAPITAL;
  const [futureYears, setFutureYears] = useState<number>(0);
  const [page, setPage] = useState(0);
  const isDaytrading = activeTab === 'Daytrading';
  const minSinceDate = earliestDates[activeTab];
  const sinceDateBeforeAvailable = minSinceDate !== undefined && sinceDate < minSinceDate;
  const effectiveStart = sinceDateBeforeAvailable ? (minSinceDate as string) : sinceDate;

  // Visible (filtered by sinceDate) — used for list + pagination; full history is used separately for CAGR projection.
  const visibleTrades = useMemo(() => {
    const cutoffMs = new Date(effectiveStart + 'T00:00:00').getTime();
    return (trades[activeTab] ?? [])
      .filter(t => new Date(t.closedAt).getTime() >= cutoffMs)
      .sort((a, b) => new Date(b.closedAt).getTime() - new Date(a.closedAt).getTime());
  }, [trades, activeTab, effectiveStart]);

  const visibleSessions = useMemo(() => {
    const cutoffMs = new Date(effectiveStart + 'T00:00:00').getTime();
    return sessions
      .filter(s => new Date(s.sessionDate).getTime() >= cutoffMs)
      .sort((a, b) => new Date(b.sessionDate).getTime() - new Date(a.sessionDate).getTime());
  }, [sessions, effectiveStart]);

  // Pagination
  const allItems = isDaytrading ? visibleSessions : visibleTrades;
  const totalItems = allItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / TRADES_PAGE_SIZE));
  const hasTradeData = totalItems > 0;

  const prevTab = useRef(activeTab);
  useEffect(() => {
    if (prevTab.current !== activeTab) {
      setPage(0);
      prevTab.current = activeTab;
    }
  }, [activeTab]);

  const pagedTrades = useMemo(() => {
    const start = page * TRADES_PAGE_SIZE;
    return visibleTrades.slice(start, start + TRADES_PAGE_SIZE);
  }, [visibleTrades, page]);

  const pagedSessions = useMemo(() => {
    const start = page * TRADES_PAGE_SIZE;
    return visibleSessions.slice(start, start + TRADES_PAGE_SIZE);
  }, [visibleSessions, page]);

  const chartData = useMemo<ChartDataPoint[]>(() => {
    const fullItems = isDaytrading
      ? sessions.map(s => ({ date: s.sessionDate, pct: s.profitPct, capitalPercent: 1 }))
      : (trades[activeTab] ?? []).map(t => ({
          date: t.closedAt,
          pct: t.yieldPercent ?? 0,
          capitalPercent: t.capitalPercent,
        }));

    const sortedAll = [...fullItems].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const startMs = new Date(effectiveStart + 'T00:00:00').getTime();
    const visible = sortedAll.filter(x => new Date(x.date).getTime() >= startMs);

    let current = capital;
    const points: ChartDataPoint[] = [{
      date: new Date(startMs).toISOString(),
      dateLabel: new Date(startMs).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }),
      dateMs: startMs,
      capital,
      capitalFuture: null,
      profit: 0,
      isFuture: false,
    }];
    for (const t of visible) {
      const next = current * (1 + (t.pct / 100) * t.capitalPercent);
      const profit = next - current;
      current = next;
      const tMs = new Date(t.date).getTime();
      points.push({
        date: new Date(tMs).toISOString(),
        dateLabel: new Date(tMs).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }),
        dateMs: tMs,
        capital: Math.round(current * 100) / 100,
        capitalFuture: null,
        profit: Math.round(profit * 100) / 100,
        isFuture: false,
      });
    }

    if (futureYears > 0 && points.length > 1 && capital > 0 && current > 0 && sortedAll.length >= 2) {
      // CAGR is computed from the ENTIRE historical set (not filtered by sinceDate),
      // so the projected growth reflects the strategy's full-period annualized return.
      let fullMultiplier = 1;
      for (const t of sortedAll) {
        fullMultiplier *= 1 + (t.pct / 100) * t.capitalPercent;
      }
      const fullStartMs = new Date(sortedAll[0].date).getTime();
      const fullEndMs = new Date(sortedAll[sortedAll.length - 1].date).getTime();
      const fullYearsSpan = (fullEndMs - fullStartMs) / MS_PER_YEAR;
      const cagr = fullYearsSpan > 0 && fullMultiplier > 0
        ? Math.pow(fullMultiplier, 1 / fullYearsSpan) - 1
        : 0;

      const lastPoint = points[points.length - 1];
      const lastMs = new Date(lastPoint.date).getTime();
      // Bridge: last historical point also anchors the future area for a seamless connection
      lastPoint.capitalFuture = lastPoint.capital;

      let projected = current;
      for (let year = 1; year <= futureYears; year++) {
        const previousProjected = projected;
        projected = projected * (1 + cagr);
        const futureMs = lastMs + year * MS_PER_YEAR;
        points.push({
          date: new Date(futureMs).toISOString(),
          dateLabel: new Date(futureMs).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }),
          dateMs: futureMs,
          capital: null,
          capitalFuture: Math.round(projected * 100) / 100,
          profit: Math.round((projected - previousProjected) * 100) / 100,
          isFuture: true,
        });
      }
    }

    return points;
  }, [activeTab, trades, sessions, isDaytrading, capital, effectiveStart, futureYears]);

  const lastHistoricalPoint = useMemo(() => {
    for (let i = chartData.length - 1; i >= 0; i--) {
      if (!chartData[i].isFuture && chartData[i].capital !== null) return chartData[i];
    }
    return null;
  }, [chartData]);

  const finalValue = lastHistoricalPoint?.capital ?? capital;
  const totalReturn = capital > 0 ? ((finalValue - capital) / capital) * 100 : 0;

  const lastFuturePoint = useMemo(() => {
    for (let i = chartData.length - 1; i >= 0; i--) {
      if (chartData[i].isFuture && chartData[i].capitalFuture !== null) return chartData[i];
    }
    return null;
  }, [chartData]);

  const projectedValue = lastFuturePoint?.capitalFuture ?? null;
  const projectedReturn = projectedValue !== null && capital > 0 ? ((projectedValue - capital) / capital) * 100 : 0;

  const chartMin = useMemo(() => {
    if (chartData.length === 0) return 0;
    const values = chartData.flatMap(d => {
      const result: number[] = [];
      if (d.capital !== null) result.push(d.capital);
      if (d.capitalFuture !== null) result.push(d.capitalFuture);
      return result;
    });
    if (values.length === 0) return 0;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.1;
    return Math.floor((min - padding) / 1000) * 1000;
  }, [chartData]);

  const ChartTooltipContent = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: ChartDataPoint }> }) => {
    if (active === true && payload !== undefined && payload.length > 0) {
      const data = payload[0].payload;
      const value = data.capital ?? data.capitalFuture;
      if (value === null) return null;
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            {data.dateLabel}
            {data.isFuture && <span className="ml-1 text-violet-500 dark:text-violet-400">(Prognose)</span>}
          </p>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            ${value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          {data.profit !== 0 && (
            <p className={`text-xs font-medium ${data.profit > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {data.profit > 0 ? '+' : ''}${data.profit.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <section className="border-t border-gray-200 dark:border-gray-800">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {!hideCampusResults && (
          <>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white text-center mb-2">
          Ergebnisse des Campus
        </h2>
        <p className="text-gray-400 dark:text-gray-400 text-center mb-8 text-sm">
          Wie hätte sich dein Kapital seit dem {new Date(sinceDate + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} entwickelt?
        </p>

        <StrategyTabs activeTab={activeTab} setActiveTab={setActiveTab} visibleTabs={visibleTabs} />

        {/* Capital & Date Inputs */}
        <div className="flex flex-wrap items-start justify-center gap-x-6 gap-y-3 mb-8">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3 h-10">
              <label className="text-sm text-gray-500 dark:text-gray-400 font-medium">Startkapital:</label>
              <NumberInput
                value={String(capital)}
                onChange={(digits) => setCapital(digits === '' ? 0 : Number(digits))}
                suffix="$"
                ariaLabel="Startkapital"
                size="sm"
                className={`inline-flex items-stretch w-36 rounded-lg border overflow-hidden bg-white dark:bg-gray-700 focus-within:ring-2 ${
                  capitalIsValid
                    ? 'border-gray-300 dark:border-gray-600 focus-within:ring-blue-500 focus-within:border-transparent'
                    : 'border-red-400 dark:border-red-500 focus-within:ring-red-500'
                }`}
              />
            </div>
            {!capitalIsValid && (
              <p className="text-xs text-red-600 dark:text-red-400 text-right">
                Mindestkapital: ${MIN_CAPITAL.toLocaleString('de-DE')}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 h-10">
            <label className="text-sm text-gray-500 dark:text-gray-400 font-medium">Berechnung ab:</label>
            <input
              type="date"
              value={sinceDate}
              onChange={e => {
                if (e.target.value !== '') onSinceDateChange(e.target.value);
              }}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
          <div className="flex items-center gap-3 h-10">
            <label htmlFor="future-years" className="text-sm text-gray-500 dark:text-gray-400 font-medium">Prognose-Zeitraum:</label>
            <input
              id="future-years"
              type="range"
              min={0}
              max={MAX_FUTURE_YEARS}
              step={1}
              value={futureYears}
              onChange={e => setFutureYears(Number(e.target.value))}
              className="w-32 cursor-pointer accent-violet-500"
              aria-label="Jahre in die Zukunft"
            />
            <span className="text-sm font-semibold text-violet-600 dark:text-violet-400 w-16">
              {futureYears} {futureYears === 1 ? 'Jahr' : 'Jahre'}
            </span>
          </div>
        </div>

        {sinceDateBeforeAvailable && (
          <p className="text-xs text-amber-600 dark:text-amber-400 text-center mb-4">
            Hinweis: Für {activeTab} sind erst ab dem {new Date((minSinceDate as string) + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} Trades verfügbar. Der Graph startet ab diesem Datum.
          </p>
        )}

        {/* Chart */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : !capitalIsValid ? (
          <p className="text-center text-gray-400 dark:text-gray-400 py-12 text-sm">
            Bitte gib mindestens ${MIN_CAPITAL.toLocaleString('de-DE')} als Startkapital ein.
          </p>
        ) : chartData.length <= 1 ? (
          <p className="text-center text-gray-400 dark:text-gray-400 py-12 text-sm">
            Keine Daten für den gewählten Zeitraum verfügbar.
          </p>
        ) : (
          <>
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 p-4" style={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="capitalGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="capitalFutureGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis
                    dataKey="dateMs"
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    scale="time"
                    tick={{ fontSize: 11, fill: 'currentColor' }}
                    className="text-gray-500 dark:text-gray-400"
                    tickMargin={8}
                    minTickGap={40}
                    tickFormatter={(ms: number) => new Date(ms).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'currentColor' }}
                    className="text-gray-500 dark:text-gray-400"
                    tickFormatter={(v: number) => `$${(v / 1000).toLocaleString('de-DE')}k`}
                    tickMargin={4}
                    domain={[chartMin, 'dataMax']}
                  />
                  <Tooltip content={<ChartTooltipContent />} />
                  <ReferenceLine y={capital} stroke="#9ca3af" strokeDasharray="4 4" strokeWidth={1} />
                  <Area
                    type="monotone"
                    dataKey="capital"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#capitalGradient)"
                    dot={false}
                    activeDot={{ r: 4, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }}
                    connectNulls={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="capitalFuture"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    fill="url(#capitalFutureGradient)"
                    dot={false}
                    activeDot={{ r: 4, fill: '#8b5cf6', stroke: '#fff', strokeWidth: 2 }}
                    connectNulls={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Summary */}
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Aus{' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  ${capital.toLocaleString('de-DE')}
                </span>
                {' '}wären{' '}
                <span className={`font-semibold ${finalValue >= capital ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  ${finalValue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                {' '}geworden{' '}
                <span className={`text-xs font-medium ${finalValue >= capital ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  ({finalValue >= capital ? '+' : ''}{totalReturn.toFixed(1)}%)
                </span>
              </p>
              {futureYears > 0 && projectedValue !== null && (
                <p className="text-sm text-violet-600 dark:text-violet-400 mt-1">
                  Prognose in {futureYears} {futureYears === 1 ? 'Jahr' : 'Jahren'}:{' '}
                  <span className="font-semibold">
                    ${projectedValue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  {' '}
                  <span className="text-xs font-medium">
                    ({projectedValue >= capital ? '+' : ''}{projectedReturn.toFixed(1)}%)
                  </span>
                </p>
              )}
              {calculatorNote !== null && (
                <p className="text-xs text-gray-400 dark:text-gray-400 mt-1">{calculatorNote}</p>
              )}
              <p className="text-xs text-gray-400 dark:text-gray-400 mt-1">
                Basierend auf historischen Ergebnissen seit dem {new Date(effectiveStart + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}. Keine Garantie für zukünftige Renditen.
              </p>
            </div>
          </>
        )}

        {/* ===== Trades Table ===== */}
        {!loading && hasTradeData && (
          <>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mt-12 mb-6">
              Letzte geschlossene Trades
            </h3>
            <div className="bg-gray-100/50 dark:bg-gray-800/50 rounded-xl border border-gray-300 dark:border-gray-700 overflow-hidden">
              {isDaytrading ? (
                <>
                  <div className="grid grid-cols-3 px-5 py-3 border-b border-gray-300 dark:border-gray-700 text-xs font-semibold text-gray-400 dark:text-gray-400 uppercase tracking-wider">
                    <span>Trades</span>
                    <span className="text-right">Ergebnis</span>
                    <span className="text-right">Datum</span>
                  </div>
                  {pagedSessions.map((session, idx) => (
                    <div
                      key={idx}
                      className={`grid grid-cols-3 px-5 py-3 items-center ${
                        idx < pagedSessions.length - 1 ? 'border-b border-gray-200/50 dark:border-gray-700/50' : ''
                      }`}
                    >
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {session.tradesCount} Trades
                      </span>
                      <span className={`text-sm font-medium text-right ${getProfitColorClass(session.profitPct)}`}>
                        {session.profitPct > 0 ? '+' : ''}{session.profitPct.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                      </span>
                      <span className="text-sm text-gray-400 dark:text-gray-400 text-right">
                        {new Date(session.sessionDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </span>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="grid grid-cols-3 px-5 py-3 border-b border-gray-300 dark:border-gray-700 text-xs font-semibold text-gray-400 dark:text-gray-400 uppercase tracking-wider">
                    <span>Symbol</span>
                    <span className="text-right">
                      {activeTab === 'Kernstrategie' || activeTab === 'Earnings' ? 'Status' : 'Rendite'}
                    </span>
                    <span className="text-right">Abgeschlossen</span>
                  </div>
                  {pagedTrades.map((trade, idx) => (
                    <div
                      key={idx}
                      className={`grid grid-cols-3 px-5 py-3 items-center ${
                        idx < pagedTrades.length - 1 ? 'border-b border-gray-200/50 dark:border-gray-700/50' : ''
                      }`}
                    >
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{trade.symbol}</span>
                      <span className={`text-sm font-medium text-right flex items-center justify-end gap-1.5 ${
                        trade.result === 'win' ? 'text-green-600 dark:text-green-400'
                          : trade.result === 'loss' ? 'text-red-600 dark:text-red-400'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {trade.yieldPercent !== null ? (
                          <>
                            <FontAwesomeIcon
                              icon={trade.result === 'loss' ? faArrowTrendDown : faArrowTrendUp}
                              className="w-3 h-3"
                            />
                            {trade.yieldPercent > 0 ? '+' : ''}{trade.yieldPercent.toFixed(trade.yieldDecimals)}%
                          </>
                        ) : (
                          <span>{trade.statusLabel}</span>
                        )}
                      </span>
                      <span className="text-sm text-gray-400 dark:text-gray-400 text-right">
                        {new Date(trade.closedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </span>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-4">
                <button
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 0}
                  className="p-2 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-default cursor-pointer transition-colors"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="w-3.5 h-3.5" />
                </button>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Seite {page + 1} von {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                  className="p-2 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-default cursor-pointer transition-colors"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {tradeTableNote !== null && (
              <p className="text-xs text-gray-400 dark:text-gray-400 text-center max-w-xl mx-auto mt-4">
                {tradeTableNote}
              </p>
            )}
          </>
        )}
          </>
        )}

        {/* Approach / CTA */}
        <div className="mt-16 border-t border-gray-200 dark:border-gray-700 pt-12">
          <div className="max-w-2xl mx-auto text-center">
            {approach.philosophyLines.map((line, idx) => (
              <p key={idx} className={`text-xl md:text-xl font-bold text-gray-500 dark:text-gray-400 ${idx === approach.philosophyLines.length - 1 ? 'mb-4' : 'mb-2'}`}>
                {line}
              </p>
            ))}
            <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-8">
              {approach.finalLine}
            </p>

            <p className="text-xl md:text-xl font-bold text-gray-500 dark:text-gray-400 mb-4">{approach.bridgeLine}</p>

            <div className="space-y-3 max-w-md mx-auto mb-8">
              {approach.bulletItems.map((text, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30">
                  <span className="text-blue-500 text-lg">→</span>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* What you get */}
        <div className="mt-16 border-t border-gray-200 dark:border-gray-700 pt-12">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white text-center mb-2">
              {whatYouGet.headline}
            </h3>
            <p className="text-xl md:text-xl font-bold text-gray-500 dark:text-gray-400 text-center mb-6">
              {whatYouGet.subheadline}
            </p>

            <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30 max-w-xl mx-auto mb-10">
              <span className="text-lg"></span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {whatYouGet.highlight}
              </span>
            </div>

            {/* Package cards */}
            {(() => {
              const visibleCards = packageFilter !== null
                ? PACKAGE_CARDS.filter(c => packageFilter.includes(c.key))
                : PACKAGE_CARDS;
              const gridCols = visibleCards.length === 1
                ? 'grid-cols-1 max-w-md'
                : visibleCards.length === 3
                  ? 'grid-cols-1 md:grid-cols-3'
                  : 'grid-cols-1 md:grid-cols-2';
              return (
                <div className={`grid ${gridCols} gap-6 mx-auto`}>
                  {visibleCards.map((card) => (
                    <div key={card.key} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-6">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3">{card.title}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                        {card.description}
                      </p>
                      <div className="space-y-2">
                        {card.items.map((t, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
                            <span className="text-gray-400 dark:text-gray-400 leading-5">•</span>
                            <span>{t}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {whatYouGet.footnote !== undefined && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-6 max-w-xl mx-auto">
                {whatYouGet.footnote}
              </p>
            )}

            {/* Klartext */}
            <div className="max-w-xl mx-auto mt-16 mb-16 text-center">
              {klartext.title !== '' && (
                <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white text-center mb-2">{klartext.title}</p>
              )}
              <p className=" text-xl md:text-2xl font-medium text-gray-500 dark:text-gray-400 mb-2">{klartext.notForYouHeader}</p>
              {(klartext.notForYouLines ?? [klartext.notForYou]).map((line, idx, arr) => (
                <p key={idx} className={`text-xl text-gray-400 dark:text-gray-400 line-through decoration-red-300/30 dark:decoration-red-500/20 ${idx === arr.length - 1 ? 'mb-8' : 'mb-2'}`}>
                  {line}
                </p>
              ))}

              <p className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-2">{klartext.forYouHeader}</p>
              {klartext.forYouLines.map((line, idx) => (
                <p key={idx} className={`text-xl font-medium text-gray-900 dark:text-white ${idx === klartext.forYouLines.length - 1 ? 'mb-6' : 'mb-2'}`}>
                  {line}
                </p>
              ))}
            </div>

            {/* Unterschied */}
            <div className="max-w-xl mx-auto mb-12 text-center">
              <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white text-center mb-8 uppercase tracking-widest">{unterschied.title}</p>

              <div className="flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-0">
                {/* Die meisten */}
                <div className="flex-1 opacity-60">
                  <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-400 font-semibold mb-3">{unterschied.theyHeader}</p>
                  {unterschied.theyItems.map((t, i) => (
                    <p key={i} className="text-gray-500 dark:text-gray-400 mb-1">{t}</p>
                  ))}
                </div>

                {/* Divider */}
                <div className="hidden md:flex flex-col items-center justify-center px-6 self-stretch">
                  <div className="w-px flex-1 bg-gray-300 dark:bg-gray-600" />
                  <span className="text-xs font-bold text-gray-400 dark:text-gray-400 my-2">VS</span>
                  <div className="w-px flex-1 bg-gray-300 dark:bg-gray-600" />
                </div>
                <div className="md:hidden w-12 h-px bg-gray-300 dark:bg-gray-600" />

                {/* Ich */}
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-wider text-blue-500 dark:text-blue-400 font-semibold mb-3">{unterschied.meHeader}</p>
                  {unterschied.meItems.map((t, i) => (
                    <p key={i} className="text-gray-900 dark:text-white font-medium mb-1">{t}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BenefitsSection({ content }: { content: LandingPageBenefits }) {
  return (
    <section className="border-t border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-950/50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white text-center mb-6">
          {content.headline}
        </h2>
        <div className="space-y-3 max-w-md mx-auto mb-8">
          {content.painPoints.map((text, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30">
              <FontAwesomeIcon icon={faXmark} className="w-4 h-4 text-red-500 dark:text-red-400 shrink-0" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{text}</span>
            </div>
          ))}
        </div>
        <p className="text-gray-900 dark:text-white text-center mb-4">
          {content.conclusion}
        </p>
        <p className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white text-center">
          {content.punchline}
        </p>
      </div>
    </section>
  );
}

function WhoTradesSection({ content }: { content: LandingPageWhoTrades }) {
  return (
    <section className="border-t border-gray-200 dark:border-gray-800">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white text-center mb-8">
          {content.headline}
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="rounded-xl overflow-hidden shadow-2xl">
            <img
              src={content.imageSrc}
              alt={content.imageAlt}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="space-y-4">
            {content.paragraphs.map((paragraph, idx) => (
              <p key={idx} className="text-gray-600 dark:text-gray-300 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection({ widgetId, headline, subline }: { widgetId: string; headline: string; subline: string }) {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://widget.senja.io/widget/${widgetId}/platform.js`;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [widgetId]);

  return (
    <section className="border-t border-gray-200 dark:border-gray-800">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white text-center mb-2">
          {headline}
        </h2>
        <p className="text-gray-400 dark:text-gray-400 text-center mb-8 text-sm">
          {subline}
        </p>
        <div
          key={widgetId}
          className="senja-embed"
          data-id={widgetId}
          data-mode="shadow"
          data-lazyload="false"
          style={{ display: 'block', width: '100%' }}
        />
      </div>
    </section>
  );
}

function FAQSection({ cheapestPrice, faqItems, ctaLabel }: { cheapestPrice: string | number; faqItems: LandingPageFaqItem[]; ctaLabel: string }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const renderedItems = faqItems.map(item => ({
    ...item,
    answer: item.answer.replace('{cheapestPrice}', String(cheapestPrice)),
  }));

  return (
    <section className="border-t border-gray-200 dark:border-gray-800">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white text-center mb-2">
          Häufige Fragen
        </h2>
        <p className="text-xl md:text-2xl font-medium text-gray-500 dark:text-gray-400 mb-4 text-center">
          Alles, was du vor der Anmeldung wissen solltest
        </p>
        <div className="space-y-3">
          {renderedItems.map((item, idx) => (
            <div
              key={idx}
              className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
              >
                <span>{item.question}</span>
                <FontAwesomeIcon
                  icon={openIndex === idx ? faChevronUp : faChevronDown}
                  className="w-4 h-4 text-gray-400 dark:text-gray-400 shrink-0 ml-4"
                />
              </button>
              {openIndex === idx && (
                <div className="px-5 pb-4 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {item.answer}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center mt-8">
          <a
            href="#pakete"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-all cursor-pointer"
          >
            {ctaLabel}
            <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDarkMode } = useDarkMode();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('6m');
  const { products, loading, error: productsError } = useSubscriptionProducts();
  // Kein einziges aktives Produkt heißt: das Backend kommt gerade nicht an Stripe. Ohne
  // diesen Fall bliebe der Preisblock kommentarlos leer.
  const noProductsAvailable = !loading && productsError === null && !products.some(p => p.active);
  const error = (productsError !== null || noProductsAvailable)
    ? 'Fehler beim Laden der Preise. Bitte versuche es später erneut.'
    : null;

  // Sticky CTA: show when hero CTA scrolls out of viewport
  const heroCTARef = useRef<HTMLAnchorElement>(null);
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  useEffect(() => {
    const el = heroCTARef.current;
    if (el === null) return;

    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyCTA(!entry.isIntersecting),
      { threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // URL-based variant: ?paket=trade|cashflow|daytrading. Default: 'all'
  const variant = useMemo<LandingPageVariant>(() => {
    return parseLandingVariant(searchParams.get('paket'));
  }, [searchParams]);

  // Optional referrer code from a general landing link (?referrer=...), carried
  // through to the registration URL so the referral survives the plan selection.
  const referrer = useMemo<string>(() => {
    const p = searchParams.get('referrer');
    return p !== null && p.trim() !== '' ? p.trim() : '';
  }, [searchParams]);

  const content = LANDING_PAGE_CONTENT[variant];

  const packageFilter = useMemo<string[] | null>(() => {
    return variant === 'all' ? null : [variant];
  }, [variant]);

  const showBothPeriods = variant !== 'all';

  // Trade state (shared between GrowthChartSection and ClosedTradesSection)
  const visibleTabs = useMemo(() => {
    if (packageFilter === null) return TRADE_TABS;
    const categories = packageFilter.flatMap(pkg => PACKAGE_TRADE_CATEGORIES[pkg] ?? []);
    return TRADE_TABS.filter(tab => categories.includes(tab));
  }, [packageFilter]);

  const [activeTab, setActiveTab] = useState<TradeCategory>(visibleTabs[0] ?? 'Swing Trade');
  const [allTrades, setAllTrades] = useState<Record<string, NormalizedTrade[]>>({});
  const [allSessions, setAllSessions] = useState<NormalizedSession[]>([]);
  const [tradesLoading, setTradesLoading] = useState(true);

  const [sinceDate, setSinceDate] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 2);
    return d.toISOString().split('T')[0];
  });

  // Earliest available trade/session date per strategy — used as min for the "Berechnung ab" date input
  const [earliestDates, setEarliestDates] = useState<Partial<Record<TradeCategory, string>>>({});

  useEffect(() => {
    if (content.hideCampusResults) return;

    const fetchEarliestDates = async () => {
      const skip = Promise.reject('skip');
      void skip.catch(() => {}); // prevent an unhandled rejection when all tabs are visible and skip is unused
      // Core/Earnings: backend ignores sort by date fields ASC, so we sort by `created` ASC as a proxy
      // for the earliest historical record and read its startDate.
      const [coreRes, earningsRes, swingRes, sessionsRes] = await Promise.allSettled([
        visibleTabs.includes('Kernstrategie') ? APIRoutes.listPublicCoreRecommendations({ sort: 'created', sortDirection: 'ASC', limit: 1 }) : skip,
        visibleTabs.includes('Earnings') ? APIRoutes.listPublicEarningsRecommendations({ sort: 'created', sortDirection: 'ASC', limit: 1 }) : skip,
        visibleTabs.includes('Swing Trade') ? APIRoutes.listPublicSwingTrades({ sort: 'startTimestamp', sortDirection: 'ASC', limit: 1 }) : skip,
        visibleTabs.includes('Daytrading') ? APIRoutes.listPublicDayTradingSessions({ sort: 'start', sortDirection: 'ASC', limit: 1 }) : skip,
      ]);

      const result: Partial<Record<TradeCategory, string>> = {};
      if (coreRes.status === 'fulfilled' && coreRes.value.recommendations.length > 0) {
        result['Kernstrategie'] = coreRes.value.recommendations[0].startDate;
      }
      if (earningsRes.status === 'fulfilled' && earningsRes.value.recommendations.length > 0) {
        result['Earnings'] = earningsRes.value.recommendations[0].startDate;
      }
      if (swingRes.status === 'fulfilled' && swingRes.value.trades.length > 0) {
        result['Swing Trade'] = swingRes.value.trades[0].startTimestamp.split('T')[0];
      }
      if (sessionsRes.status === 'fulfilled' && sessionsRes.value.sessions.length > 0) {
        result['Daytrading'] = sessionsRes.value.sessions[0].start.split('T')[0];
      }

      setEarliestDates(result);
    };

    fetchEarliestDates();
  }, [visibleTabs, content.hideCampusResults]);

  useEffect(() => {
    if (content.hideCampusResults) {
      setTradesLoading(false);
      return;
    }

    const fetchAllTrades = async () => {
      setTradesLoading(true);

      const skip = Promise.reject('skip');
      void skip.catch(() => {}); // prevent an unhandled rejection when all tabs are visible and skip is unused
      const [coreRes, earningsRes, swingRes, longTermRes, sessionsRes] = await Promise.allSettled([
        visibleTabs.includes('Kernstrategie') ? APIRoutes.listPublicCoreRecommendations({ sort: 'endDate', sortDirection: 'DESC', limit: 500 }) : skip,
        visibleTabs.includes('Earnings') ? APIRoutes.listPublicEarningsRecommendations({ sort: 'endDate', sortDirection: 'DESC', limit: 500 }) : skip,
        visibleTabs.includes('Swing Trade') ? APIRoutes.listPublicSwingTrades({ sort: 'updated', sortDirection: 'DESC', limit: 500 }) : skip,
        visibleTabs.includes('Langzeit') ? APIRoutes.listPublicLongTermTrades({ sort: 'updated', sortDirection: 'DESC', limit: 500 }) : skip,
        visibleTabs.includes('Daytrading') ? APIRoutes.listPublicDayTradingSessions({ open: false, sort: 'start', sortDirection: 'ASC', limit: 500 }) : skip,
      ]);

      const result: Record<string, NormalizedTrade[]> = {};

      if (coreRes.status === 'fulfilled') {
        result['Kernstrategie'] = coreRes.value.recommendations.map(normalizeCoreRecommendation);
      }
      if (earningsRes.status === 'fulfilled') {
        result['Earnings'] = earningsRes.value.recommendations.map(normalizeEarningsRecommendation);
      }
      if (swingRes.status === 'fulfilled') {
        result['Swing Trade'] = swingRes.value.trades
          .filter(t => t.state === 'FINISHED_PROFIT' || t.state === 'FINISHED_LOSS')
          .map(normalizeSwingTrade);
      }
      if (longTermRes.status === 'fulfilled') {
        result['Langzeit'] = longTermRes.value.trades.map(normalizeLongTermTrade);
      }
      if (sessionsRes.status === 'fulfilled') {
        setAllSessions(sessionsRes.value.sessions.map(normalizeSession));
      }

      setAllTrades(result);
      setTradesLoading(false);
    };

    fetchAllTrades();
  }, [visibleTabs, content.hideCampusResults]);

  const cheapestMonthlyPrice = useMemo(() => {
    const visiblePackages = LANDING_PACKAGES.filter(pkg => {
      if (packageFilter !== null && !packageFilter.includes(pkg.name.toLowerCase())) return false;
      return true;
    });
    const visibleProductTypes = visiblePackages.map(pkg => pkg.productType);
    const relevantProducts = products.filter(p =>
      p.active
      && p.productType !== null
      && visibleProductTypes.includes(p.productType)
      && (p.intervalMonths === 1 || p.intervalMonths === 6)
    );
    if (relevantProducts.length === 0) return 29;
    const cheapestPerMonth = Math.min(...relevantProducts.map(p => p.price / p.intervalMonths / 100));
    return cheapestPerMonth.toFixed(2).replace('.', ',');
  }, [products, packageFilter]);

  const getProductForPeriod = (productType: string, period: BillingPeriod): SubscriptionPriceProduct | undefined => {
    const months = getBillingPeriodMonths(period);
    return products.find(p => p.productType === productType && p.intervalMonths === months && p.active);
  };

  const getMonthlyBasePrice = (productType: string): EuroCent | undefined => {
    const monthlyProduct = products.find(p => p.productType === productType && p.intervalMonths === 1);
    return monthlyProduct?.price;
  };

  const handleSelectPlan = (type: SubscriptionType) => {
    analytics.track(AnalyticsEvents.PLAN_SELECTED, { selectedPlan: type, source: 'landing' });
    // Die Paket-Auswahl merkt sich Registration.tsx selbst (sessionStorage 'pendingCheckout')
    const referrerQuery = referrer !== '' ? `&referrer=${encodeURIComponent(referrer)}` : '';
    navigate(`/auth/register?plan=${type}${referrerQuery}`);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
      {/* ===== Header ===== */}
      <header className="border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <img
            src={isDarkMode ? "/logo_light.png" : "/logo.png"}
            alt="Börsencampus"
            className="h-10"
          />
          <div className="flex items-center gap-2 sm:gap-3">
            <DarkModeToggle />
            <Link
              to="/login"
              className="sm:inline-block text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors font-medium"
            >
              Login
            </Link>
            {/*<a*/}
            {/*  href="#preise"*/}
            {/*  className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-all cursor-pointer"*/}
            {/*>*/}
            {/*  <span className="hidden sm:inline">Jetzt starten</span>*/}
            {/*  <span className="sm:hidden">Starten</span>*/}
            {/*</a>*/}
          </div>
        </div>
      </header>

      {/* ===== Hero Section ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left: Text + Social Proof */}
          <div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              {content.hero.headline}
            </h1>
            <p className="text-lg md:text-xl text-blue-600 dark:text-blue-400 font-medium mb-6 leading-relaxed">
              {content.hero.subheadlineLines.map((line, idx) => (
                <span key={idx}>
                  {line}
                  {idx < content.hero.subheadlineLines.length - 1 && <br />}
                </span>
              ))}
            </p>
            <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
              {content.hero.descriptionLines.map((line, idx) => (
                <span key={idx}>
                  {line}
                  {idx < content.hero.descriptionLines.length - 1 && <br />}
                </span>
              ))}
            </p>

            {/* Social Proof: Rating + Mitglieder */}
            <div className="flex items-center gap-4 flex-wrap mb-6">
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4].map((i) => (
                    <FontAwesomeIcon key={i} icon={faStar} className="w-4 h-4 text-yellow-400" />
                  ))}
                  <FontAwesomeIcon icon={faStarHalfStroke} className="w-4 h-4 text-yellow-400" />
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">(4,9/5)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-gray-900 dark:text-white">4.9</span>
                <a
                  href="https://www.provenexpert.com/de-de/sven-bolz1/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 transition-colors leading-tight"
                >
                  Bewertungen auf<br />
                  <span className="font-semibold text-gray-600 dark:text-gray-300">ProvenExpert</span>
                </a>
              </div>
              {/* Mitgliederzahl */}
              <div className="flex items-center gap-2 sm:border-l sm:border-gray-300 dark:sm:border-gray-600 sm:pl-4">
                <span className="text-2xl font-bold text-gray-900 dark:text-white">1.000+</span>
                <span className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
                  aktive<br />
                  <span className="font-semibold text-gray-600 dark:text-gray-300">Mitglieder</span>
                </span>
              </div>
            </div>

            {/* Hero CTA */}
            <a
              href="#pakete"
              ref={heroCTARef}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-bold text-white bg-linear-to-r from-blue-600 to-blue-500 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 hover:shadow-xl hover:-translate-y-0.5 transition-all cursor-pointer"
            >
              {content.hero.ctaLabel}
              <FontAwesomeIcon icon={faArrowRight} className="w-5 h-5" />
            </a>
            <p className="text-xs text-gray-400 dark:text-gray-400 mt-2 mb-6 flex items-center gap-1.5">
              <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3 text-green-500" />
              Jederzeit kündbar – ab {cheapestMonthlyPrice} € / Monat
            </p>

            {/* Trust Logos */}
            <div>
              <p className="text-xs text-gray-400 dark:text-gray-400 mb-2 font-medium">
                Bekannt aus & Partner von
              </p>
              <div className="flex items-center gap-5 flex-wrap">
                <img src={logoXtb} alt="xtb" className="h-6 sm:h-5 grayscale dark:invert opacity-70" />
                <img src={logoTradingView} alt="TradingView" className="h-5.5 sm:h-5 grayscale dark:invert opacity-70" />
                <img src={logoBitpanda} alt="Bitpanda" className="h-5.5 sm:h-5 grayscale dark:invert opacity-70" />
                <img src={logoIBKR} alt="Interactive Brokers" className="h-5.5 sm:h-5 grayscale dark:invert opacity-70" />
                <img src={logoHamburg1} alt="Hamburg 1" className="h-6 sm:h-5 grayscale dark:invert opacity-70" />
              </div>
            </div>
          </div>

          {/* Right: Bunny Video Embed */}
          <div className="aspect-video rounded-xl overflow-hidden bg-gray-200 dark:bg-gray-800 shadow-2xl">
            <iframe
              src={content.heroVideoUrl}
              title="Willkommen im Börsencampus"
              loading="lazy"
              allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
              allowFullScreen
              className="w-full h-full"
              style={{ border: 0 }}
            />
          </div>
        </div>
      </section>

      {/* ===== Benefits Section ===== */}
      <BenefitsSection content={content.benefits} />

      {/* ===== Results Section (Chart + Trades) ===== */}
      <ResultsSection
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        visibleTabs={visibleTabs}
        trades={allTrades}
        sessions={allSessions}
        loading={tradesLoading}
        packageFilter={packageFilter}
        sinceDate={sinceDate}
        onSinceDateChange={setSinceDate}
        earliestDates={earliestDates}
        hideCampusResults={content.hideCampusResults}
        approach={content.approach}
        whatYouGet={content.whatYouGet}
        klartext={content.klartext}
        unterschied={content.unterschied}
        calculatorDefaultCapital={content.calculatorDefaultCapital}
        calculatorNote={content.calculatorNote}
        tradeTableNote={content.tradeTableNote}
      />

      {/* ===== Pricing Section ===== */}
      <section id="preise" className="border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white text-center mb-12">
              {content.pricingIntro.mainHeadline}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-0 max-w-3xl mx-auto mb-12">
              {/* Vorher */}
              <div className="md:pr-8">
                <p className="text-sm uppercase tracking-wider text-red-400 dark:text-red-400 font-semibold mb-4">{content.pricingIntro.beforeHeader}</p>
                <div className="space-y-3">
                  {content.pricingIntro.beforeItems.map((text, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30">
                      <FontAwesomeIcon icon={faXmark} className="w-4 h-4 text-red-500 dark:text-red-400 shrink-0" />
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile Divider */}
              <div className="md:hidden w-12 h-px bg-gray-300 dark:bg-gray-600 mx-auto" />

              {/* Nachher */}
              <div className="md:pl-8 md:border-l md:border-gray-200 md:dark:border-gray-700">
                <p className="text-sm uppercase tracking-wider text-green-500 dark:text-green-400 font-semibold mb-4">{content.pricingIntro.afterHeader}</p>
                <div className="space-y-3">
                  {content.pricingIntro.afterItems.map((text, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/30">
                      <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 text-green-500 dark:text-green-400 shrink-0" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Billing Toggle */}
            {showBothPeriods && (
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white text-center mb-6">
                Wähle deine Laufzeit
              </h3>
            )}

            {!showBothPeriods ? (
              <div
                id="pakete"
                className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full p-1 border border-gray-300 dark:border-gray-700 mx-auto w-fit scroll-mt-8"
              >
                <button
                  onClick={() => setBillingPeriod('1m')}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                    billingPeriod === '1m'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  1 Monat
                </button>
                <button
                  onClick={() => setBillingPeriod('6m')}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                    billingPeriod === '6m'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  6 Monate
                  <span className="ml-2 text-xs">-10%</span>
                </button>
              </div>
            ) : (
              <div id="pakete" className="scroll-mt-8" />
            )}

            {/* Promotion Banner */}
            {LANDING_PROMOTION_CONFIG !== null && isPromotionActive(LANDING_PROMOTION_CONFIG) && (
              <PromotionBanner config={LANDING_PROMOTION_CONFIG} />
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-20">
              <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          )}

          {/* Error State */}
          {error !== null && (
            <div className="max-w-xl mx-auto">
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-6 border border-red-200 dark:border-red-800 text-center">
                <p className="text-red-600 dark:text-red-400">{error}</p>
              </div>
            </div>
          )}

          {/* Pricing Grid */}
          {!loading && error === null && (() => {
            const periodsToShow: BillingPeriod[] = showBothPeriods ? ['1m', '6m'] : [billingPeriod];

            const isPackageAvailable = (config: typeof LANDING_PACKAGES[number]) =>
              periodsToShow.every(period =>
                getProductForPeriod(config.productType, period) !== undefined
              );

            const visiblePackages = LANDING_PACKAGES.filter((config) => {
              if (packageFilter !== null && !packageFilter.includes(config.name.toLowerCase())) return false;
              return isPackageAvailable(config);
            });

            const mainPackages = visiblePackages.filter(p => p.separate !== true);
            const separatePackages = visiblePackages.filter(p => p.separate === true);

            const renderPackageCards = (config: typeof LANDING_PACKAGES[number]) =>
              periodsToShow.map(period => {
                const product = getProductForPeriod(config.productType, period)!;
                return (
                  <PublicPricingCard
                    key={`${config.productType}-${period}`}
                    product={product}
                    billingPeriod={period}
                    config={config}
                    monthlyBasePrice={period !== '1m' ? getMonthlyBasePrice(config.productType) : undefined}
                    onSelectPlan={handleSelectPlan}
                    periodLabel={showBothPeriods ? (period === '1m' ? '1 Monat' : '6 Monate') : undefined}
                    periodLabelSuffix={showBothPeriods && period === '6m' ? '-10%' : undefined}
                  />
                );
              });

            const mainCardCount = mainPackages.length * periodsToShow.length;
            const mainGridCols = mainCardCount === 1
              ? 'grid-cols-1 max-w-md'
              : mainCardCount === 2
                ? 'grid-cols-1 md:grid-cols-2 max-w-3xl'
                : 'grid-cols-1 md:grid-cols-3 max-w-5xl';

            const separateGridCols = separatePackages.length * periodsToShow.length === 2
              ? 'grid-cols-1 md:grid-cols-2 max-w-3xl'
              : 'grid-cols-1 max-w-md';

            return (
              <>
                {/* Main Packages */}
                {mainPackages.length > 0 && (
                  <div className={`grid ${mainGridCols} gap-6 mx-auto items-stretch`}>
                    {mainPackages.flatMap(renderPackageCards)}
                  </div>
                )}

                {/* Separate Packages (Daytrading) */}
                {separatePackages.length > 0 && (
                  <div className={mainPackages.length > 0 ? 'mt-10' : ''}>
                    {mainPackages.length > 0 && (
                      <>
                        <div className="w-16 h-px bg-gray-200 dark:bg-gray-700 mx-auto mb-6" />
                        <p className="text-xs uppercase tracking-widest text-gray-400 dark:text-gray-400 font-semibold text-center mb-6">Separat buchbar</p>
                      </>
                    )}
                    <div className={`grid ${separateGridCols} mx-auto gap-6 items-stretch`}>
                      {separatePackages.flatMap(renderPackageCards)}
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {/* 100K Community */}
          <div className="mt-14 max-w-3xl mx-auto text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {content.goDeeper.headline}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
              {content.goDeeper.body}
            </p>
            <a
              href="https://www.svenbolz.com/tradingausbildung-100k-community"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-all cursor-pointer"
            >
              {content.goDeeper.ctaLabel}
              <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
            </a>
          </div>

          {/* Closing Slogan */}
          <div className="mt-20 max-w-3xl mx-auto text-center">
            <div className="mb-10">
              {content.closing.sloganLines.map((line, idx) => (
                <p key={idx} className={`text-3xl md:text-5xl font-black text-gray-900 dark:text-white leading-tight tracking-tight${idx > 0 ? ' mt-2' : ''}`}>
                  {line.text}
                  {line.highlight !== '' && (
                    <>
                      {' '}
                      <span className="bg-linear-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
                        {line.highlight}
                      </span>
                    </>
                  )}
                </p>
              ))}
            </div>
            <a
              href="#pakete"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 text-base font-bold text-white bg-linear-to-r from-blue-600 to-blue-500 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 hover:shadow-xl hover:-translate-y-0.5 transition-all cursor-pointer"
            >
              {content.closing.ctaLabel}
              <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ===== Wer da eigentlich handelt ===== */}
      {content.whoTrades !== null && <WhoTradesSection content={content.whoTrades} />}

      {/* ===== Testimonials ===== */}
      {content.hideTestimonials === false && (
        <TestimonialsSection
          widgetId={content.senjaWidgetId}
          headline={content.testimonialsHeadline}
          subline={content.testimonialsSubline}
        />
      )}

      {/* ===== FAQ ===== */}
      <FAQSection cheapestPrice={cheapestMonthlyPrice} faqItems={content.faqItems} ctaLabel={content.faqCtaLabel} />

      {/* ===== Footer ===== */}
      <footer className="border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-gray-400 dark:text-gray-400">
              Powered by SvenBolz Trading Academy
            </p>
            <div className="flex flex-wrap items-center gap-4 text-xs">
              <Link to="/impressum" className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 transition-colors">
                Impressum
              </Link>
              <Link to="/datenschutz" className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 transition-colors">
                Datenschutz
              </Link>
              <Link to="/agb" className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 transition-colors">
                AGB
              </Link>
              <Link to="/risikodisclaimer" className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 transition-colors">
                Risikohinweis
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* ===== Sticky Mobile CTA ===== */}
      {showStickyCTA && (
        <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-800 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <a
            href="#pakete"
            className="flex items-center justify-center gap-2 w-full px-6 py-3.5 text-base font-bold text-white bg-linear-to-r from-blue-600 to-blue-500 rounded-xl shadow-lg cursor-pointer"
          >
            {content.closing.ctaLabel}
            <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
          </a>
        </div>
      )}
    </div>
  );
}
